/**
 * One Prepared contract diff for Diagnostics and first-run.
 *
 * Expected comes from CMMD `data.preparedContract` on the generated
 * `ForgeRuntimePreparedContract` type. Forge inventory command lists are not
 * an input.
 */

import type {
  CmmdRuntimeDiagnosticsSnapshot,
  ForgeRuntimePreparedContract,
} from "@cmmd-forge/contracts";

export type PreparedContractAuthority =
  | "snapshot"
  | "machine-inventory"
  | "provider-inventory"
  | "probe";

export type PreparedContractMatch = "match" | "miss" | "unpublished";

export interface PreparedContractRow {
  readonly name: string;
  readonly expected: string;
  readonly actual: string;
  readonly authority: PreparedContractAuthority;
  readonly match: PreparedContractMatch;
}

export interface PreparedContractActuals {
  readonly guestForgeCommit: string | null;
  readonly supervisedServiceName: string | null;
  readonly clis: ReadonlyArray<{ readonly name: string; readonly present: boolean }>;
  readonly chromiumPresent: boolean | null;
  readonly providerBinaries: ReadonlyArray<{ readonly name: string; readonly present: boolean }>;
  readonly replicationAvailable: boolean | null;
}

const UNPUBLISHED_ROWS: ReadonlyArray<{
  readonly name: string;
  readonly authority: PreparedContractAuthority;
}> = [
  { name: "Guest Forge commit", authority: "snapshot" },
  { name: "Supervised service", authority: "snapshot" },
  { name: "CLI node", authority: "machine-inventory" },
  { name: "CLI bun", authority: "machine-inventory" },
  { name: "CLI git", authority: "machine-inventory" },
  { name: "CLI playwright", authority: "machine-inventory" },
  { name: "Chromium", authority: "machine-inventory" },
  { name: "Provider binary codex", authority: "provider-inventory" },
  { name: "Provider binary claude", authority: "provider-inventory" },
  { name: "Replication", authority: "snapshot" },
];

function displayActual(value: string | boolean | null): string {
  if (value === null) return "not observed";
  if (value === true) return "present";
  if (value === false) return "missing";
  return value;
}

function cliPresent(actuals: PreparedContractActuals, name: string): boolean | null {
  const found = actuals.clis.find((cli) => cli.name === name);
  return found ? found.present : null;
}

function providerPresent(actuals: PreparedContractActuals, name: string): boolean | null {
  const found = actuals.providerBinaries.find((binary) => binary.name === name);
  return found ? found.present : null;
}

function unpublishedRow(
  name: string,
  authority: PreparedContractAuthority,
  actual: string,
): PreparedContractRow {
  return {
    name,
    expected: "not published",
    actual,
    authority,
    match: "unpublished",
  };
}

function publishedRow(
  name: string,
  expected: string,
  actual: string,
  authority: PreparedContractAuthority,
  matches: boolean,
): PreparedContractRow {
  return {
    name,
    expected,
    actual,
    authority,
    match: matches ? "match" : "miss",
  };
}

function unpublishedRowsFromActuals(
  actuals: PreparedContractActuals,
): ReadonlyArray<PreparedContractRow> {
  return UNPUBLISHED_ROWS.map((row) => {
    if (row.name === "Guest Forge commit") {
      return unpublishedRow(row.name, row.authority, displayActual(actuals.guestForgeCommit));
    }
    if (row.name === "Supervised service") {
      return unpublishedRow(row.name, row.authority, displayActual(actuals.supervisedServiceName));
    }
    if (row.name === "Chromium") {
      return unpublishedRow(row.name, row.authority, displayActual(actuals.chromiumPresent));
    }
    if (row.name === "Replication") {
      return unpublishedRow(row.name, row.authority, displayActual(actuals.replicationAvailable));
    }
    if (row.name.startsWith("CLI ")) {
      return unpublishedRow(
        row.name,
        row.authority,
        displayActual(cliPresent(actuals, row.name.slice("CLI ".length))),
      );
    }
    if (row.name.startsWith("Provider binary ")) {
      return unpublishedRow(
        row.name,
        row.authority,
        displayActual(providerPresent(actuals, row.name.slice("Provider binary ".length))),
      );
    }
    return unpublishedRow(row.name, row.authority, "not observed");
  });
}

export function expectedFromSnapshot(
  snapshot: CmmdRuntimeDiagnosticsSnapshot | undefined,
): ForgeRuntimePreparedContract | null {
  if (snapshot === undefined) return null;
  return snapshot.preparedContract ?? null;
}

export function diffPreparedContract(input: {
  readonly expected: ForgeRuntimePreparedContract | null;
  readonly actuals: PreparedContractActuals;
}): ReadonlyArray<PreparedContractRow> {
  const actuals = input.actuals;
  if (input.expected === null) {
    return unpublishedRowsFromActuals(actuals);
  }

  const expected = input.expected;
  const cliRows = expected.requiredClis.map((cli) => {
    const present = cliPresent(actuals, cli.name);
    return publishedRow(
      `CLI ${cli.name}`,
      "required",
      displayActual(present),
      "machine-inventory",
      present === true,
    );
  });
  const providerRows = expected.providerBinaries.map((binary) => {
    const present = providerPresent(actuals, binary.name);
    return publishedRow(
      `Provider binary ${binary.name}`,
      "required",
      displayActual(present),
      "provider-inventory",
      present === true,
    );
  });
  const expectedCommit = expected.guestForgeCommit;
  const commitRow =
    expectedCommit === null
      ? unpublishedRow("Guest Forge commit", "snapshot", displayActual(actuals.guestForgeCommit))
      : publishedRow(
          "Guest Forge commit",
          expectedCommit,
          displayActual(actuals.guestForgeCommit),
          "snapshot",
          actuals.guestForgeCommit === expectedCommit,
        );
  return [
    commitRow,
    publishedRow(
      "Supervised service",
      expected.supervisedService.name,
      displayActual(actuals.supervisedServiceName),
      "snapshot",
      actuals.supervisedServiceName === expected.supervisedService.name,
    ),
    ...cliRows,
    publishedRow(
      "Chromium",
      expected.chromium.variant,
      actuals.chromiumPresent === true
        ? expected.chromium.variant
        : displayActual(actuals.chromiumPresent),
      "machine-inventory",
      actuals.chromiumPresent === true,
    ),
    ...providerRows,
    publishedRow(
      "Replication",
      expected.replication.required ? "required" : "not required",
      displayActual(actuals.replicationAvailable),
      "snapshot",
      actuals.replicationAvailable === true,
    ),
  ];
}

export function preparedContractHasFailingRows(rows: ReadonlyArray<PreparedContractRow>): boolean {
  return rows.some((row) => row.match === "miss");
}

export function actualsFromDiagnostics(input: {
  readonly guestForgeCommit: string | null | undefined;
  readonly supervisedServiceObserved: boolean;
  readonly toolNames: ReadonlyArray<{ readonly name: string; readonly ok: boolean }>;
  readonly replicationAvailable: boolean | null;
}): PreparedContractActuals {
  const chromium = input.toolNames.find(
    (entry) => entry.name === "chromium-headless-shell" || entry.name === "chromium",
  );
  return {
    guestForgeCommit: input.guestForgeCommit ?? null,
    supervisedServiceName: input.supervisedServiceObserved ? "forge" : null,
    clis: input.toolNames.map((tool) => ({ name: tool.name, present: tool.ok })),
    chromiumPresent: chromium ? chromium.ok : null,
    providerBinaries: input.toolNames.flatMap((tool) =>
      tool.name === "codex" || tool.name === "claude"
        ? [{ name: tool.name, present: tool.ok }]
        : [],
    ),
    replicationAvailable: input.replicationAvailable,
  };
}

export function firstRunSucceeds(input: {
  readonly expected: ForgeRuntimePreparedContract | null;
  readonly environmentId: string | null;
  readonly rows: ReadonlyArray<PreparedContractRow>;
}): boolean {
  if (input.environmentId === null || input.environmentId.length === 0) return false;
  if (input.expected === null) return true;
  return !input.rows.some((row) => row.match === "miss" && row.actual !== "not observed");
}

export function firstRunSucceedsWithUnpublishedContract(input: {
  readonly expected: ForgeRuntimePreparedContract | null;
  readonly environmentId: string | null;
}): boolean {
  return input.expected === null && input.environmentId !== null && input.environmentId.length > 0;
}
